// Factory that returns a google.maps.OverlayView subclass.
// Call only AFTER window.google.maps has loaded — OverlayView isn't defined
// before that.

const METERS_PER_FOOT = 0.3048;

/** Web-Mercator meters-per-pixel at a given latitude and zoom. */
function metersPerPixel(latDeg, zoom) {
  return (156543.03392 * Math.cos((latDeg * Math.PI) / 180)) / Math.pow(2, zoom);
}

/**
 * @returns {new (opts: {position: {lat,lng}, text: string, rotationDeg: number, color: string, heightFeet?: number}) => google.maps.OverlayView}
 */
export function createIsdLabelOverlayViewClass() {
  const g = window.google?.maps;
  if (!g || !g.OverlayView) {
    throw new Error('google.maps.OverlayView not available yet');
  }

  return class IsdLabelOverlayView extends g.OverlayView {
    constructor({ position, text, rotationDeg, color, heightFeet = 2 }) {
      super();
      this._position = position;
      this._text = text;
      this._rotationDeg = rotationDeg;
      this._color = color;
      this._heightFeet = heightFeet;
      this._div = null;
    }

    onAdd() {
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.pointerEvents = 'none';
      div.style.whiteSpace = 'nowrap';
      // ISOCPEUR is the TrueType variant of the AutoCAD ISOCP font (ships with
      // AutoCAD / Windows-with-AutoCAD installs). Fallback chain covers
      // systems without it.
      div.style.fontFamily = '"ISOCPEUR", "ISOCP", "isocp", "Helvetica Narrow", "Arial Narrow", ui-sans-serif, system-ui, sans-serif';
      // Font-size is computed each draw() from the map's zoom so the label
      // scales with the circles. Initial value is a placeholder.
      div.style.fontSize = '12px';
      div.style.fontWeight = '900';
      div.style.letterSpacing = '0.02em';
      div.style.color = this._color;
      // Crisp black outline via -webkit-text-stroke (paint-order: stroke fill
      // ensures the stroke is painted UNDER the fill, so the fill isn't eaten
      // away). Stroke width is recomputed in draw() to scale with font size.
      div.style.setProperty('paint-order', 'stroke fill');
      // Tiny 1px shadow as a safety net for any browser without text-stroke.
      div.style.textShadow = '0 0 1px #000';
      div.style.transformOrigin = '50% 50%';
      div.style.zIndex = '9999';
      div.textContent = this._text;
      this._div = div;
      // Use floatPane so labels always render above circles/polylines,
      // which live in overlayLayer below this pane.
      const panes = this.getPanes();
      if (panes) (panes.floatPane || panes.overlayLayer).appendChild(div);
    }

    draw() {
      if (!this._div) return;
      const proj = this.getProjection();
      if (!proj) return;
      const pos = proj.fromLatLngToDivPixel(
        new g.LatLng(this._position.lat, this._position.lng),
      );
      if (!pos) return;
      // Real-world sizing: N ft of text maps to X pixels given current zoom.
      const map = this.getMap();
      const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : 20;
      const mpp = metersPerPixel(this._position.lat, zoom);
      const pxPerFoot = METERS_PER_FOOT / mpp;
      const fontPx = Math.max(1, this._heightFeet * pxPerFoot);
      this._div.style.fontSize = `${fontPx}px`;
      // Stroke ~10% of font height, with a 1.5 px floor and 8 px ceiling.
      const strokePx = Math.min(8, Math.max(1.5, fontPx * 0.1));
      this._div.style.setProperty('-webkit-text-stroke', `${strokePx}px #000`);
      this._div.style.left = `${pos.x}px`;
      this._div.style.top = `${pos.y}px`;
      this._div.style.transform = `translate(-50%, -50%) rotate(${this._rotationDeg}deg)`;
    }

    onRemove() {
      if (this._div && this._div.parentNode) {
        this._div.parentNode.removeChild(this._div);
      }
      this._div = null;
    }

    /** Mutate without recreating. Caller must re-`draw()` the map if needed. */
    update({ position, text, rotationDeg, color, heightFeet }) {
      if (position) this._position = position;
      if (typeof text === 'string') this._text = text;
      if (Number.isFinite(rotationDeg)) this._rotationDeg = rotationDeg;
      if (color) this._color = color;
      if (Number.isFinite(heightFeet)) this._heightFeet = heightFeet;
      if (this._div) {
        if (color) this._div.style.color = color;
        if (typeof text === 'string') this._div.textContent = text;
      }
      this.draw();
    }
  };
}
